<template>
  <!-- eslint-disable vue/component-name-in-template-casing -->
  <!-- eslint-disable vue/no-v-html -->
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <header class="pt-9">
    <Container>
      <div class="grid gap-3 2xl:gap-36 md:grid-cols-2 md:pb-24">
        <!-- IMAGES -->
        <div>
          <!-- Icons -->
          <Pictograms :data="product"/>

          <!-- Slider Images -->
          <swiper-container
              ref="sliderRef"
              class="customs-swiper-style flex max-w-[calc(100vw-48px)] transition-colors"
              :class="isClient ? 'opacity-100' : 'opacity-0'"
          >
            <swiper-slide
                v-for="image in productImages"
                :key="image.id"
                class=""
            >
              <div class="px-[10%] py-3 2xl:px-28 md:px-[15%] md:py-9">
                <div class="flex aspect-[350/338] w-full items-center">
                  <AppImage class="size-full object-contain" :alt="image.alt" :src="image.url"/>
                </div>
              </div>
            </swiper-slide>
          </swiper-container>
        </div>

        <!-- TEXTS -->
        <div class="pb-24 pt-0 md:pb-0 md:pt-12">
          <!-- Colorized Bar -->

          <ClientOnly>
            <div v-if="isChemistry && accentColor" class="mb-3 h-2 w-full sm:w-1/3" :class="accentColor"/>
          </ClientOnly>

          <!-- Category -->
          <div class="mb-2 text-grey-300 typo-body-sm">
            {{ getTranslatedProperty(category, 'name') }}
          </div>

          <!-- Title -->
          <h1 class="mb-2 font-semibold typo-h1">
            {{ getTranslatedProperty(product, 'name') }}
          </h1>

          <!-- Art No. / sell unit -->
          <div class="mb-9 text-grey-300 typo-body-sm">
            <span>{{ $t('product.sku') }} {{ product.productNumber }}</span>
            <span class="ml-9">{{ productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit }}</span>
          </div>

          <!-- VARIANT SELECTOR WET-200 -->
          <div v-if="hasVariants" class="bg-grey-50 py-4">
            <div class="flex flex-col flex-wrap align-start gap-2 sm:flex-nowrap sm:gap-4">
              <label for="variant-selector" class="display-block">
                {{ $t('pdp_variant_selector_label') }}
              </label>

              <!-- Variant selector: inline-block container for select and SVG -->
              <div class="relative flex">
                <select
                    id="variant-selector"
                    class="inline-block w-auto max-w-full appearance-none rounded border border-grey-300 bg-white px-3 py-2 pr-10 text-sm"
                    @change="navigateToVariant"
                >
                  <!-- current product as first, disabled -->
                  <option selected disabled :value="product.id">
                    {{ product.translated?.name || product.name }}<template v-if="product?.translated?.customFields?.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit">, {{ product.translated.customFields.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit }}</template>
                  </option>

                  <option
                      v-for="variant in variantsData.products.filter(v => v.id !== product.id)"
                      :key="variant.id"
                      :value="variant.id"
                  >
                    {{ variant.translated?.name || variant.name }}<template v-if="variant?.translated?.customFields?.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit">, {{ variant.translated.customFields.PIM_PRODUCT_CUSTOM_FIELD_verkaufseinheit }}</template>
                  </option>
                </select>
                <svg
                    class="pointer-events-none relative mt-3 right-7 size-4 text-grey-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="mt-3 typo-body" v-html="productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_aufmachertxt"/>

          <!-- Properties -->
          <div class="wysiwyg-only-lists-check-mark wysiwyg mt-7 typo-body checkmark ml-2"
               v-html="productTranslatedCustomFields.PIM_PRODUCT_CUSTOM_FIELD_eigenschaftentxt"/>

          <!-- Shop Button -->
          <AppButton target="_blank" :to="productUrl" class="mt-8">
            {{ $t('to_webshop') }}
          </AppButton>
        </div>
      </div>
    </Container>
  </header>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup lang="ts">
import {getTranslatedProperty, getProductRoute} from '@shopware/helpers'
import {Navigation, Pagination} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import type {Schemas} from '#shopware'

const {t, localeProperties} = useI18n()

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const sliderRef = ref(null)
const isClient = ref(false)

const product = computed(() => {
  return props.data || {}
})

const productTranslatedCustomFields = computed(() => {
  return product.value.translated?.customFields || {}
})

const accentColor = computed(() => {
  const term = productTranslatedCustomFields.value?.PIM_PRODUCT_CUSTOM_FIELD_hauptanwendung
  return useColorMapping(term)
})

const config = useRuntimeConfig()

// Currently not in use until the new shop is live
const productRoute = computed(() => {
  const endpoint = config.public.shopwareBaseRoute
  const path = getProductRoute(product.value)?.path

  return `${endpoint}${path}`
})

const coverImage = computed(() => {
  if (!product.value.cover?.media) {
    return null
  }

  const {media} = product.value.cover
  return {
    id: media.id,
    alt: media.translated.alt,
    url: media.url,
  }
})

const productImages = computed(() => {
  if (!Array.isArray(product.value.media) || product.value.media.length === 0) {
    return []
  }

  let images = product.value.media.map((media) => {
    return {
      id: media.media.id,
      alt: media.media.translated.alt,
      url: media.media.url,
    }
  })

  // If there is a cover image, filter out from the images and add it as the first image
  if (coverImage.value) {
    images = images.filter(image => image.id !== coverImage.value?.id)
  }

  images.unshift(coverImage.value)

  return images
})

useSwiper(sliderRef, {
  modules: [Navigation, Pagination],
  slidesPerView: 1,
  allowTouchMove: true,
  navigation: true,
  pagination: {
    clickable: true,
  },
})

const categories = inject<Schemas['Category'][] | null>('categories', null)

const category = computed<Schemas['Category'] | null>(() => {
  // return product.value.seoCategory
  if (!Array.isArray(product.value.categoryTree) || product.value.categoryTree.length < 2) {
    return null
  }

  // The current category is the second last element in the categoryTree
  return categories?.value?.find(category => category.id === product.value.categoryTree[product.value.categoryTree.length - 2]) || null
})

const isChemistry = computed(() => {
  if (!Array.isArray(product.value?.categoryTree) || product.value?.categoryTree.length < 2) {
    return false
  }
  return product.value?.categoryTree[1] === '0195b8e86b6c721d999bb1d2ca18606b' // This is the chemistry category
})

// TODO: Remove this when the new shop is live
const productUrl = await useLegacyProductUrl(product.value.productNumber)

// VARIANTS
const variantsData = computed(() => {
  const crossSellings = product?.value?.crossSellings || []

  const variantCrossSelling = crossSellings.find(item =>
      item.crossSelling.translated.name.toLowerCase() === 'varianten')

  return variantCrossSelling || null
})

const variantPaths = computed(() => {
  const paths: Record<string, string> = {}

  const variants = variantsData.value?.products || []
  variants.forEach((variant) => {
    const path = useGetProductPathAsync(variant, localeProperties.value)
    paths[variant.id] = typeof path === 'string' ? path : `/products/${variant.id}`
  })

  return paths
})

const hasVariants = computed(() => {
  return variantsData.value && variantsData.value.products && variantsData.value.products.length > 0
})

const navigateToVariant = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const variantId = target.value

  if (variantId && variantPaths.value[variantId]) {
    const localePath = useLocalePath()
    const url = `/${variantPaths.value[variantId]}`

    const route = localePath({
      name: 'products-all',
      params: {all: url.split('/')?.filter(Boolean)},
    })

    navigateTo(route)
  }
}

onMounted(() => {
  isClient.value = true
})
</script>

<style>
.customs-swiper-style {
  --swiper-theme-color: #007A81;
  --swiper-navigation-size: 1.5rem;
  --swiper-pagination-bullet-width: 1.75rem;
  --swiper-pagination-bullet-height: 0.15rem;
  --swiper-pagination-bullet-border-radius: 0;
  --swiper-pagination-bullet-inactive-color: #000;
  --swiper-pagination-bullet-inactive-opacity: 1;
  --swiper-navigation-color: black;
}

.checkmark {
  li::before {
    top: 0;
  }
}
</style>
